import type { Feed, FeedItem } from '@knocklabs/client'
import type { JsonObject } from '@seen/core/json'
import { InboxItemType, isInboxItem } from '@seen/core/models/inbox'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { resolveId } from '@seen/core/references'
import { requestInboxFeed } from '#src/hooks/inbox/inboxFeedRequest.ts'
import { getStrictGroupKey } from '#src/hooks/inbox/useInboxDedupedItems.ts'
import { KNOCK_PAGE_SIZE } from '#src/hooks/useNotificationItems.ts'

// Convergence for the rows already on screen, without ever replacing the
// store. A no-cursor fetch() collapses the list to page one and moves the row
// being read; these helpers instead read the same page store-free and apply
// only what it positively asserts: a status that changed, an item that came
// back. Absence from the page is never treated as removal.

const STATUS_FIELDS = [
  'read_at',
  'seen_at',
  'interacted_at',
  'archived_at',
] as const

export async function fetchInboxPage(
  feedClient: Feed
): Promise<FeedItem[] | undefined> {
  const response = await requestInboxFeed(feedClient, {
    page_size: KNOCK_PAGE_SIZE,
  })
  return response?.entries
}

// Walks the feed from the top, newest-first, until it runs out or hits the page
// cap. Store-free like fetchInboxPage: the result is for the coordinator's own
// bookkeeping, never for the list, whose pagination stays the SDK's. Returns
// what it got when a later page fails; undefined only when the first one does.
export async function fetchInboxPages(
  feedClient: Feed,
  maxPages: number
): Promise<FeedItem[] | undefined> {
  const entries: FeedItem[] = []
  let after: string | undefined
  for (let page = 0; page < maxPages; page++) {
    const overrides: JsonObject = { page_size: KNOCK_PAGE_SIZE }
    if (after !== undefined) {
      overrides.after = after
    }
    const response = await requestInboxFeed(feedClient, overrides)
    // A walk that stopped short is not a walk: the caller retires what it did
    // not see and would stop retrying, so a failed page fails the whole read.
    if (!response) {
      return undefined
    }
    entries.push(...response.entries)
    after = response.page_info.after ?? undefined
    if (after === undefined) {
      break
    }
  }
  return entries
}

export interface PatchInboxItemsResult {
  updated: number
  reappeared: number
}

// Patches read/seen/interacted state onto items already in the store and
// re-inserts items the page holds that the store does not — an item un-done
// elsewhere, or one the socket missed while another request was in flight.
// Items are kept newest-first, matching the feed's own order.
export function patchInboxItems(
  feedClient: Feed,
  entries: FeedItem[]
): PatchInboxItemsResult {
  let updated = 0
  let reappeared = 0
  feedClient.store.setState((state) => {
    const byId = new Map(entries.map((entry) => [entry.id, entry]))
    let changed = false
    const items = state.items.map((item) => {
      const fresh = byId.get(item.id)
      if (!fresh) {
        return item
      }
      byId.delete(item.id)
      if (STATUS_FIELDS.every((field) => item[field] === fresh[field])) {
        return item
      }
      changed = true
      updated++
      return { ...item, ...pick(fresh, STATUS_FIELDS) }
    })
    if (byId.size === 0) {
      return changed ? { ...state, items } : state
    }
    reappeared = byId.size
    const merged = [...items, ...byId.values()].sort((a, b) =>
      b.inserted_at.localeCompare(a.inserted_at)
    )
    return { ...state, items: merged }
  })
  return { updated, reappeared }
}

function pick<T, K extends keyof T>(source: T, keys: readonly K[]): Pick<T, K> {
  const out = {} as Pick<T, K>
  for (const key of keys) {
    out[key] = source[key]
  }
  return out
}

// The task a task-family notification points at. Only these types carry one;
// comment-family notifications on a task key by thread and are never hidden
// by the closed-task filter, so they are not candidates for the sweep.
export function taskIdOf(item: FeedItem): string | undefined {
  const data = item.data
  if (!isInboxItem(data)) {
    return undefined
  }
  switch (data.type) {
    case InboxItemType.TaskAssigned:
    case InboxItemType.TaskDue:
    case InboxItemType.TaskNewComment:
      return resolveId(data.task)
    default:
      return undefined
  }
}

// Notifications whose task the server says is closed. The inbox hides these
// rows but Knock still counts them, which is exactly the "badge says 2, list
// is empty" report. Strictly positive: a task the lookup did not return is
// left alone — it may be deleted, or the lookup may simply have failed, and
// only the former would justify archiving.
export function findOrphanedTaskItems(
  items: FeedItem[],
  tasks: StandardTask[]
): FeedItem[] {
  const closed = new Set(
    tasks.flatMap((task) =>
      task.isCompletedOrCancelled || task.isDeleted ? [task.id] : []
    )
  )
  return items.filter((item) => {
    const taskId = taskIdOf(item)
    return taskId !== undefined && closed.has(taskId)
  })
}

// The conversation a notification belongs to, when that can be proven. Null
// means "never supersede this one" — see getStrictGroupKey.
export function supersedeKeyOf(item: FeedItem): string | null {
  const data = item.data
  return isInboxItem(data) ? getStrictGroupKey(data) : null
}

// One live message per conversation: within each strict group everything but
// the newest is superseded. The inbox already shows one row per group, so these
// are invisible on screen while Knock still counts them in the badge; archiving
// them makes the count equal the rows. Ordered newest-first with the id as the
// tie-break so two tabs computing this agree on which message survives.
export function findSupersededItems(items: FeedItem[]): FeedItem[] {
  const groups = new Map<string, FeedItem[]>()
  for (const item of items) {
    if (item.archived_at) {
      continue
    }
    const key = supersedeKeyOf(item)
    if (key === null) {
      continue
    }
    const group = groups.get(key) ?? []
    group.push(item)
    groups.set(key, group)
  }
  const superseded: FeedItem[] = []
  for (const group of groups.values()) {
    if (group.length < 2) {
      continue
    }
    group.sort(
      (a, b) =>
        b.inserted_at.localeCompare(a.inserted_at) || b.id.localeCompare(a.id)
    )
    superseded.push(...group.slice(1))
  }
  return superseded
}
